import React, { useEffect, useState } from "react";
import {
  ShoppingCart,
  Mail,
  FileText,
  Send,
  CheckCircle2,
  CalendarCheck2,
  Wallet,
  CalendarClock,
  CalendarDays,
  CalendarX2,
  Ban,
  TrendingUp,
  UtensilsCrossed,
  FolderTree,
} from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";

/**
 * Real Module-1 tiles show live counts. Future-module tiles show honest zero states
 * with a "Coming soon" label — no fake business data (per LOCKED spec).
 */
const REAL_TILES = [
  { key: "dishes", label: "Dishes", icon: UtensilsCrossed },
  { key: "categories", label: "Categories", icon: FolderTree },
  { key: "new_enquiries", label: "New Enquiries", icon: Mail },
];

const PLACEHOLDER_TILES = [
  { key: "new_orders", label: "New Orders", icon: ShoppingCart },
  { key: "pending_quotes", label: "Pending Quotes", icon: FileText },
  { key: "quotes_sent", label: "Quotes Sent", icon: Send },
  { key: "accepted_quotes", label: "Accepted Quotes", icon: CheckCircle2 },
  { key: "confirmed_bookings", label: "Confirmed Bookings", icon: CalendarCheck2 },
  { key: "advance_pending", label: "Advance Pending", icon: Wallet },
  { key: "upcoming_events", label: "Upcoming Events", icon: CalendarClock },
  { key: "todays_events", label: "Today's Events", icon: CalendarDays },
  { key: "completed_events", label: "Completed Events", icon: CalendarX2 },
  { key: "cancelled", label: "Cancelled", icon: Ban },
  { key: "revenue", label: "Revenue", icon: TrendingUp },
];

export default function AdminDashboard() {
  const { t } = useApp();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/dashboard/stats");
        setStats(data);
      } catch (e) {
        console.warn(e);
      }
    })();
  }, []);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex items-end justify-between gap-6">
        <div>
          <span className="kbd-eyebrow">{t("admin.dashboard")}</span>
          <h1 className="mt-3 font-serif-display text-3xl font-semibold text-secondary">
            Welcome back
          </h1>
        </div>
      </div>

      {/* Real tiles */}
      <h2 className="mt-8 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        Live (Module 1)
      </h2>
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-real-tiles">
        {REAL_TILES.map((t2) => (
          <Tile
            key={t2.key}
            icon={t2.icon}
            label={t2.label}
            value={stats?.[t2.key] ?? "—"}
            testid={`admin-tile-${t2.key}`}
            live
          />
        ))}
      </div>

      {/* Placeholder future-module tiles */}
      <h2 className="mt-10 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        {t("admin.coming_soon")}
      </h2>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="admin-placeholder-tiles">
        {PLACEHOLDER_TILES.map((t2) => (
          <Tile
            key={t2.key}
            icon={t2.icon}
            label={t2.label}
            value={0}
            testid={`admin-tile-${t2.key}`}
            placeholder
          />
        ))}
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, testid, live = false, placeholder = false }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-5 ${
        placeholder
          ? "border-dashed border-border/70 bg-card/60"
          : "border-border/70 bg-card"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div
          className={`grid h-9 w-9 place-items-center rounded-xl ${
            placeholder ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"
          }`}
        >
          <Icon className="h-4 w-4" />
        </div>
        {live && (
          <span className="rounded-full bg-accent/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-secondary">
            Live
          </span>
        )}
      </div>
      <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 font-serif-display text-3xl font-semibold text-secondary">{value}</div>
    </div>
  );
}
